/** @jsxImportSource @emotion/react */

import React, { useState } from "react";
import {
  EuiButtonEmpty,
  EuiContextMenuPanel,
  EuiContextMenuItem,
  EuiPopover,
  useGeneratedHtmlId,
  EuiIcon,
  useEuiTheme,
  shade,
  tint,
} from "@inscopix/ideas-eui";
import { keyBy } from "lodash";
import { css } from "@emotion/react";

type ToggleFilterItem = {
  name: string;
  value: string;
  first: "asc" | "desc";
};

type SelectedItem<T extends { value: string }> = {
  item: T["value"];
  state: "asc" | "desc";
};

export interface ToggleFilterProps<T extends readonly ToggleFilterItem[]> {
  items: T;
  selected: SelectedItem<T[number]>;
  onChange: (selected: SelectedItem<T[number]>) => void;
}

export const ToggleFilter = <T extends readonly ToggleFilterItem[]>({
  items,
  selected,
  onChange,
}: ToggleFilterProps<T>) => {
  const { euiTheme, colorMode } = useEuiTheme();

  // match button background to role filter group button
  const backgroundColor =
    colorMode === "DARK"
      ? shade(euiTheme.colors.lightestShade, 0.4)
      : tint(euiTheme.colors.lightestShade, 0.6);

  const itemsByValue = keyBy(items, (item) => item.value);

  const [isPopoverOpen, setPopover] = useState(false);

  const singleContextMenuPopoverId = useGeneratedHtmlId({
    prefix: "singleContextMenuPopover",
  });

  const onButtonClick = () => {
    setPopover((isPopoverOpen) => !isPopoverOpen);
  };

  const closePopover = () => {
    setPopover(false);
  };

  const selectedItemIcon = selected.state === "asc" ? "sortUp" : "sortDown";
  const selectedItemName = itemsByValue[selected.item].name;

  const button = (
    <EuiButtonEmpty
      size="m"
      iconType="arrowDown"
      iconSide="right"
      onClick={onButtonClick}
      color="text"
      css={css`
        font-weight: 500;
        border: 1px solid ${euiTheme.colors.lightShade};
        background-color: ${backgroundColor};
      `}
    >
      {<EuiIcon type={selectedItemIcon} />} <span>{selectedItemName}</span>
    </EuiButtonEmpty>
  );

  return (
    <EuiPopover
      id={singleContextMenuPopoverId}
      button={button}
      isOpen={isPopoverOpen}
      closePopover={closePopover}
      panelPaddingSize="none"
      anchorPosition="downLeft"
    >
      <EuiContextMenuPanel
        size="s"
        items={items.map((item) => {
          return (
            <EuiContextMenuItem
              key={item.value}
              icon={selected.item === item.value ? selectedItemIcon : "empty"}
              onClick={() => {
                const isSelected = selected.item === item.value;

                if (isSelected) {
                  const next = selected.state === "asc" ? "desc" : "asc";
                  return onChange({ item: selected.item, state: next });
                } else {
                  return onChange({ item: item.value, state: item.first });
                }
              }}
            >
              {item.name}
            </EuiContextMenuItem>
          );
        })}
      />
    </EuiPopover>
  );
};
